import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../../context/UserContext";
import Map from "../../components/Map"; // aggiorna il path se necessario
import Search from "../../components/Search"; // il componente di ricerca custom
import FilterDropDown from "../../components/FilterDropDown";
import CreateActivityButton from "../../components/buttons/CreateActivityButton";
import UserBadge from "../../components/buttons/UserBadge";
import LogoutButton from "../../components/buttons/LogoutButton";
import LegendBox from "../../components/LegendBox";
import CalendarStatsBox from "../../components/CalendarStatsBox";
import NotificationCenter from "../../components/NotificationCenter";
import ActivityOverview from "../../components/ActivityOverview";
import ReportBox from "../../components/ReportBox";
import api from "../../Api";

// Funzione per filtrare le attività in base al range di date selezionato.
function filterActivitiesByDate(activities, startDate, endDate) {
  if (!startDate || !endDate) return activities;
  return activities.filter((activity) => {
    const activityDate = new Date(activity.start_date);
    return activityDate >= startDate && activityDate <= endDate;
  });
}

export default function Dashboard() {
  const { user } = useContext(UserContext);
  const [mapPosition, setMapPosition] = useState(null);
  const [activities, setActivities] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState([]);

  // Recupera le attività dall'API
  useEffect(() => {
    if (!user) return; // Attendi che l'utente sia caricato

    let endpoint = "/activity/activities";
    let config = {};

    if (user.role === "rup") {
      endpoint = "/activity/worksite-activities";
      config = { params: { worksite: user.worksite } }; // aggiungi il parametro worksite
    }

    api
      .get(endpoint, config)
      .then((res) => {
        setActivities(res.data);
      })
      .catch((err) => {
        console.error("Errore nel fetching delle attività:", err);
      });
  }, [user]);

  const handleSearch = async (query) => {
    if (!query) return;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}`
      );
      const data = await res.json();
      if (data && data[0]) {
        const { lat, lon } = data[0];
        setMapPosition({ lat: parseFloat(lat), lng: parseFloat(lon) });
      }
    } catch (error) {
      console.error("Errore durante la ricerca dell'indirizzo:", error);
    }
  };

  // Funzione per aggiornare il range di date (viene chiamata da CalendarStatsBox)
  const handleDateRangeChange = (start, end) => {
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  };

  // Filtra prima per range di date...
  const filteredByDate = filterActivitiesByDate(activities, selectedStartDate, selectedEndDate);

  // ... e poi per tipo di attività (activity_names)
  const filteredActivities = filteredByDate.filter((activity) => {
    // Se non hai selezionato nessun filtro, restituisci tutte le attività
    if (selectedFilters.length === 0) return true;
    // Verifica se almeno uno dei filtri selezionati è presente in activity.activity_names
    return selectedFilters.some((filter) => activity.activity_names.includes(filter));
  });

  return (
    <div className="min-h-screen bg-global p-12">
      {/* Header */}
      <header className="flex justify-between items-center mb-6">
        <div className="flex space-x-2 items-center">
          <FilterDropDown
            onFilterChange={(filters) => {
              setSelectedFilters(filters);
            }}
          />
          <Search onSearch={handleSearch} />
        </div>
        <div className="flex space-x-2 items-center">
          <CreateActivityButton />
          <UserBadge />
          <LogoutButton />
        </div>
      </header>

      {/* Dashboard Grid */}
      <main className="grid grid-cols-4 gap-6 auto-rows-fr">
        {/* Mappa con marker per le attività filtrate */}
        <div className="col-span-2 bg-white rounded-2xl shadow overflow-hidden max-h-[530px]">
          <Map
            position={mapPosition}
            setPosition={setMapPosition}
            activities={filteredActivities}
          />
        </div>
        <div className="col-span-1">
          <CalendarStatsBox onDateRangeChange={handleDateRangeChange} />
        </div>
        <div className="col-span-1">
          <NotificationCenter />
        </div>
        <div className="col-span-1">
          <LegendBox />
        </div>
        <div className="col-span-2 rounded-2xl shadow">
          <ActivityOverview
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
          />
        </div>
        <div className="col-span-1 rounded-2xl shadow">
          <ReportBox />
        </div>
      </main>
    </div>
  );
}
